/**
 * Subscription Offer Block JavaScript
 * Handles tier selection, dynamic pricing calculations, and add to cart functionality
 */

class SubscriptionOfferBlock {
  constructor(blockElement) {
    this.block = blockElement;
    this.blockId = blockElement.dataset.blockId;
    this.basePrice = parseFloat(blockElement.dataset.basePrice) || 0;
    this.productId = blockElement.dataset.productId;
    this.subscriptionEnabled = blockElement.dataset.subscriptionEnabled === 'true';
    this.subscriptionDiscount = parseFloat(blockElement.dataset.subscriptionDiscount) || 0;
    this.sellingPlanId = blockElement.dataset.sellingPlanId || null;
    this.sellingPlanIdTier = blockElement.dataset.sellingPlanIdTier || null;

    this.selectedTier = null;
    this.isSubscription = true; // Default to subscription

    this.init();
  }

  init() {
    this.cacheDOMElements();
    this.bindEvents();
    this.selectDefaultTier();

    // Set flag to prevent variant picker from overwriting our prices
    const quantityBreakContainer = document.querySelector('.quantity-break-container');
    if (quantityBreakContainer) {
      quantityBreakContainer.setAttribute('data-custom-price', 'true');
    }

    // Set up MutationObserver to re-apply our prices if another script changes them
    this.setupPriceProtection();
  }

  setupPriceProtection() {
    // Store our calculated prices so we can re-apply them
    this.lastCalculatedPrice = null;
    this.lastComparePrice = null;

    // Create a single observer callback that re-applies all prices
    const reapplyPrices = () => {
      if (this.lastCalculatedPrice !== null && this.priceBlockEl) {
        if (this.priceBlockEl.textContent !== this.lastCalculatedPrice) {
          this.priceBlockEl.textContent = this.lastCalculatedPrice;
        }
      }
      if (this.lastComparePrice !== null && this.comparePriceEl) {
        if (this.comparePriceEl.textContent !== this.lastComparePrice) {
          this.comparePriceEl.textContent = this.lastComparePrice;
        }
      }
    };

    // Observe changes to the price element
    if (this.priceBlockEl) {
      this.priceObserver = new MutationObserver(reapplyPrices);
      this.priceObserver.observe(this.priceBlockEl, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    // Observe changes to the compare price element
    if (this.comparePriceEl) {
      this.comparePriceObserver = new MutationObserver(reapplyPrices);
      this.comparePriceObserver.observe(this.comparePriceEl, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  cacheDOMElements() {
    this.tierCards = this.block.querySelectorAll('.tier-card');
    this.addToCartButton = this.block.querySelector('.subscription-add-to-cart');
    this.oneTimePurchaseLink = this.block.querySelector('.one-time-purchase-link');
    this.originalTotalEl = this.block.querySelector('[data-original-total]');
    this.discountedTotalEl = this.block.querySelector('[data-discounted-total]');

    // Hide the pricing from the CTA button
    const buttonPricing = this.block.querySelector('.button-pricing');
    if (buttonPricing) {
      buttonPricing.style.display = 'none';
    }

    // Find the separate price block on the page (outside subscription block)
    this.priceBlockEl = document.querySelector('.shop-product-price-block');
    this.comparePriceEl = document.querySelector('.shop-compare-price');
    this.saveBadgeEl = document.querySelector('.shop-save-price');
  }

  bindEvents() {
    // Tier selection events
    this.tierCards.forEach(card => {
      card.addEventListener('click', () => this.handleTierSelection(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleTierSelection(card);
        }
      });
    });

    // Add to cart event
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', () => this.handleAddToCart());
    }

    // One-time purchase event
    if (this.oneTimePurchaseLink) {
      this.oneTimePurchaseLink.addEventListener('click', () => this.handleOneTimePurchase());
    }
  }

  selectDefaultTier() {
    const defaultTier = this.block.querySelector('.tier-card.selected');
    if (defaultTier) {
      this.handleTierSelection(defaultTier, false);
    } else if (this.tierCards.length > 0) {
      this.handleTierSelection(this.tierCards[0], false);
    }

    // Force price update on page load (with slight delay to ensure DOM is ready)
    this.updatePricingDisplay();
    // Also update after delays to override any other scripts (variant picker runs at 100ms)
    setTimeout(() => this.updatePricingDisplay(), 150);
    setTimeout(() => this.updatePricingDisplay(), 300);
  }

  handleTierSelection(selectedCard, updateUI = true) {
    if (updateUI) {
      // Remove selected class from all tiers
      this.tierCards.forEach(card => {
        card.classList.remove('selected');
        card.setAttribute('aria-pressed', 'false');
      });

      // Add selected class to clicked tier
      selectedCard.classList.add('selected');
      selectedCard.setAttribute('aria-pressed', 'true');
    }

    // Get tier data
    this.selectedTier = {
      tier: selectedCard.dataset.tier,
      quantity: parseInt(selectedCard.dataset.quantity) || 1,
      discount: parseFloat(selectedCard.dataset.discount) || 0,
      basePrice: parseFloat(selectedCard.dataset.basePrice) || this.basePrice
    };

    // Update pricing display
    this.updatePricingDisplay();

    // Sync selling plan and quantity into the main product form
    // so product-form.js includes them when submitting
    this.syncToProductForm();
  }

  syncToProductForm() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // Determine the selling plan to use
    let sellingPlanValue = '';
    if (this.subscriptionEnabled && this.isSubscription) {
      const planToUse = this.selectedTier && this.selectedTier.tier === '1' && this.sellingPlanIdTier
        ? this.sellingPlanIdTier
        : this.sellingPlanId;
      if (planToUse) {
        sellingPlanValue = planToUse;
      }
    }

    // Sync selling_plan input
    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (sellingPlanValue) {
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        form.appendChild(sellingPlanInput);
      }
      sellingPlanInput.value = sellingPlanValue;
    } else if (sellingPlanInput) {
      sellingPlanInput.remove();
    }

    // Sync quantity input
    if (this.selectedTier) {
      let quantityInput = form.querySelector('input[name="quantity"]');
      if (!quantityInput) {
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        form.appendChild(quantityInput);
      }
      quantityInput.value = this.selectedTier.quantity;
    }
  }

  updatePricingDisplay() {
    if (!this.selectedTier) return;

    // Re-query price elements in case they weren't ready during initial cache
    if (!this.priceBlockEl) {
      this.priceBlockEl = document.querySelector('.shop-product-price-block');
    }
    if (!this.comparePriceEl) {
      this.comparePriceEl = document.querySelector('.shop-compare-price');
    }
    if (!this.saveBadgeEl) {
      this.saveBadgeEl = document.querySelector('.shop-save-price');
    }

    const { quantity, discount } = this.selectedTier;

    // Calculate prices
    const discountMultiplier = (100 - discount) / 100;
    let discountedPricePerUnit = this.basePrice * discountMultiplier;
    const totalOriginalPrice = this.basePrice * quantity;
    let totalDiscountedPrice = discountedPricePerUnit * quantity;

    // Apply subscription discount if enabled
    if (this.subscriptionEnabled && this.isSubscription) {
      const subscriptionMultiplier = (100 - this.subscriptionDiscount) / 100;
      totalDiscountedPrice = totalDiscountedPrice * subscriptionMultiplier;
      discountedPricePerUnit = discountedPricePerUnit * subscriptionMultiplier;
    }

    // Update button pricing (totals)
    if (this.originalTotalEl) {
      this.originalTotalEl.textContent = this.formatMoney(totalOriginalPrice);
    }
    if (this.discountedTotalEl) {
      this.discountedTotalEl.textContent = this.formatMoney(totalDiscountedPrice);
    }

    // Update the separate price block (total price for selected quantity)
    if (this.priceBlockEl) {
      const formattedPrice = this.formatMoney(totalDiscountedPrice);
      // Store for MutationObserver to re-apply if another script changes it
      this.lastCalculatedPrice = formattedPrice;
      this.priceBlockEl.textContent = formattedPrice;
      // Add on-sale class if there's a discount
      if (discount > 0 || (this.subscriptionEnabled && this.isSubscription)) {
        this.priceBlockEl.classList.add('on-sale');
      } else {
        this.priceBlockEl.classList.remove('on-sale');
      }
    }

    // Update compare-at price (show original total price when discounted)
    if (this.comparePriceEl) {
      if (discount > 0 || (this.subscriptionEnabled && this.isSubscription)) {
        const formattedComparePrice = this.formatMoney(totalOriginalPrice);
        // Store for MutationObserver to re-apply if another script changes it
        this.lastComparePrice = formattedComparePrice;
        this.comparePriceEl.textContent = formattedComparePrice;
        this.comparePriceEl.style.display = '';
      } else {
        this.lastComparePrice = null;
        this.comparePriceEl.style.display = 'none';
      }
    }

    // Update one-time purchase link with original total price
    if (this.oneTimePurchaseLink) {
      const currentText = this.oneTimePurchaseLink.textContent;
      // Replace the price in parentheses with the original total price
      const newText = currentText.replace(/\(\$[\d,.]+\)/, `(${this.formatMoney(totalOriginalPrice)})`);
      this.oneTimePurchaseLink.textContent = newText;
    }

    // Update save badge with total discount percentage
    if (this.saveBadgeEl) {
      // Calculate total discount percentage
      let totalDiscountPercent = discount;
      if (this.subscriptionEnabled && this.isSubscription) {
        // Combined discount: 1 - (1 - tierDiscount) * (1 - subDiscount)
        const combinedMultiplier = (1 - discount / 100) * (1 - this.subscriptionDiscount / 100);
        totalDiscountPercent = Math.round((1 - combinedMultiplier) * 100);
      }

      if (totalDiscountPercent > 0) {
        // Only update text nodes, preserve the SVG icon
        const textNodes = [];
        this.saveBadgeEl.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            textNodes.push(node);
          }
        });

        // If we can find text nodes with percentage, update them
        let updated = false;
        textNodes.forEach(node => {
          if (node.textContent.includes('%')) {
            node.textContent = node.textContent.replace(/\d+%/, `${totalDiscountPercent}%`);
            updated = true;
          }
        });

        // If no direct text node found, try to find nested text
        if (!updated) {
          const allText = this.saveBadgeEl.innerHTML;
          if (allText.includes('%')) {
            this.saveBadgeEl.innerHTML = allText.replace(/(\d+)%/, `${totalDiscountPercent}%`);
          }
        }

        this.saveBadgeEl.style.display = '';
      } else {
        this.saveBadgeEl.style.display = 'none';
      }
    }
  }

  handleAddToCart() {
    if (!this.selectedTier) {
      console.error('No tier selected');
      return;
    }

    // Disable button and show loading state
    this.addToCartButton.disabled = true;
    this.addToCartButton.classList.add('loading');

    // Get the product's first available variant
    const variantId = this.block.dataset.variantId || this.productId;

    // Prepare form data
    const item = {
      id: variantId,
      quantity: this.selectedTier.quantity,
      properties: {
        '_tier': `Tier ${this.selectedTier.tier}`,
        '_discount': `${this.selectedTier.discount}%`,
        '_subscription': this.isSubscription ? 'Yes' : 'No'
      }
    };

    // Add selling plan if subscription is enabled and active
    if (this.subscriptionEnabled && this.isSubscription) {

      console.log('📅 Subscription enabled, preparing to add selling plan.');
      console.log('📅 Selected tier:', this.selectedTier);
      console.log('SellingPlanIdTier1', this.sellingPlanIdTier);
      // Use tier 1 selling plan (second plan) for 3-pack, otherwise use default
      const planToUse = this.selectedTier.tier === '1' && this.sellingPlanIdTier
        ? this.sellingPlanIdTier
        : this.sellingPlanId;

      if (planToUse) {
        item.selling_plan = parseInt(planToUse, 10);
        console.log('📅 Adding selling plan to subscription:', planToUse, 'for tier', this.selectedTier.tier);
      }
    }

    // Get sections to update (for cart drawer refresh)
    // Use funnel-cart-drawer section if on a funnel page, otherwise cart-drawer
    const cartDrawer = document.querySelector('funnel-cart-drawer') || document.querySelector('cart-drawer');
    const sectionId = document.querySelector('funnel-cart-drawer') ? 'funnel-cart-drawer' : 'cart-drawer';
    const sections = [sectionId, 'cart-icon-bubble'];

    const formData = {
      items: [item],
      sections: sections,
      sections_url: window.location.pathname
    };

    // Debug: log the full request payload
    const requestBody = JSON.stringify(formData);
    console.log('🛒 [ATC Debug] Cart drawer found:', cartDrawer?.tagName || 'NONE');
    console.log('🛒 [ATC Debug] Section ID:', sectionId);
    console.log('🛒 [ATC Debug] Request payload:', requestBody);
    console.log('🛒 [ATC Debug] Item details:', JSON.stringify(item, null, 2));

    // Add to cart via Shopify Cart API
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: requestBody
    })
      .then(response => {
        console.log('🛒 [ATC Debug] Response status:', response.status, response.statusText);
        return response.json().then(data => {
          if (!response.ok) {
            console.error('🛒 [ATC Debug] Error response body:', JSON.stringify(data, null, 2));
            throw new Error(data.description || data.message || 'Failed to add to cart');
          }
          return data;
        });
      })
      .then(parsedState => {
        console.log('🛒 [ATC Debug] Success response:', JSON.stringify(parsedState, null, 2).substring(0, 500));
        console.log('🛒 [ATC Debug] Has sections?', !!parsedState.sections);

        if (cartDrawer && cartDrawer.renderContents) {
          console.log('🛒 [ATC Debug] Calling renderContents on', cartDrawer.tagName);
          cartDrawer.renderContents(parsedState);
        } else {
          console.warn('🛒 [ATC Debug] No cart drawer found to render into');
          // Fallback: try cart notification
          const cartNotification = document.querySelector('cart-notification');
          if (cartNotification && cartNotification.renderContents) {
            cartNotification.renderContents(parsedState);
          }
        }

        // Trigger cart update event for theme
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Also try window-level event
        if (window.Shopify && window.Shopify.onCartUpdate) {
          window.Shopify.onCartUpdate();
        }

        // Re-enable button
        this.addToCartButton.disabled = false;
        this.addToCartButton.classList.remove('loading');

        // Show success feedback
        this.showSuccessFeedback();
      })
      .catch(error => {
        console.error('🛒 [ATC Debug] Catch error:', error.message);
        this.addToCartButton.disabled = false;
        this.addToCartButton.classList.remove('loading');

        // Show error feedback
        this.showErrorFeedback(error.message);
      });
  }

  handleOneTimePurchase() {
    // Toggle subscription mode off
    this.isSubscription = false;

    // Update pricing to reflect one-time purchase
    this.updatePricingDisplay();

    // Sync to form (removes selling_plan since isSubscription is false)
    this.syncToProductForm();

    // Trigger add to cart with one-time purchase
    this.handleAddToCart();
  }

  showSuccessFeedback() {
    // You can customize this based on your theme's feedback pattern
    const originalText = this.addToCartButton.querySelector('.button-text').textContent;
    this.addToCartButton.querySelector('.button-text').textContent = 'Added!';

    setTimeout(() => {
      this.addToCartButton.querySelector('.button-text').textContent = originalText;
    }, 2000);
  }

  showErrorFeedback(message) {
    // You can customize this based on your theme's error handling pattern
    const originalText = this.addToCartButton.querySelector('.button-text').textContent;
    this.addToCartButton.querySelector('.button-text').textContent = 'Error - Try Again';

    console.error('Add to cart error:', message);

    setTimeout(() => {
      this.addToCartButton.querySelector('.button-text').textContent = originalText;
    }, 3000);
  }

  formatMoney(cents) {
    // Simple money formatting - you may want to use Shopify's formatMoney helper
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

}

// Initialize all subscription offer blocks on the page
document.addEventListener('DOMContentLoaded', () => {
  const subscriptionBlocks = document.querySelectorAll('.subscription-offer-block');

  subscriptionBlocks.forEach(block => {
    new SubscriptionOfferBlock(block);
  });
});

// Re-initialize if new blocks are added dynamically
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          const newBlocks = node.querySelectorAll?.('.subscription-offer-block') || [];
          newBlocks.forEach(block => {
            if (!block.dataset.initialized) {
              new SubscriptionOfferBlock(block);
              block.dataset.initialized = 'true';
            }
          });
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}
